/**
 * Landing Page — /lp/:slug
 *
 * Renders a full-page funnel experience for a given landing page slug.
 * Evaluates the funnel step for the 'landing_page' page type and presents
 * the matched offer directly inline — hero image, pricing, CTA, and decline.
 *
 * Flow:
 *  1. Mount → useFunnelExperience evaluates step for pageType='landing_page'
 *  2. If an offer is matched → render hero + price + CTA + decline link
 *  3. Accept → acceptOffer API + addToCart + navigate to /checkout
 *  4. Decline → navigate to /
 *  5. No match → render a friendly "no offers" fallback
 */

'use client';

import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import {
  useFunnelExperience,
  useFunnelContext,
  FunnelPageContent,
  ActionExecutor,
  ListenerBinder,
} from '@template-project/storefront-funnel';
import {
  CampaignErrorBoundary,
  CampaignExperience,
  extractFreeGiftItem,
  type RuntimeCampaign,
} from '@template-project/storefront-campaign';
import type { FunnelOffer, StepAction, StepListener, StepPageContent, StepPageBlock } from '@template-project/storefront-funnel';
import { useCartContext } from '@/lib/context/cart-context';
import { useFunnelAddToCart } from '@/lib/hooks/use-funnel-add-to-cart';
import { useOfferImage } from '@/lib/hooks/use-offer-image';
import { useIntl } from '@/lib/intl';
import { storefrontLayoutLogger } from '@/lib/logger';

type TemplateLoadState = 'idle' | 'loading' | 'ready' | 'failed';

// ---------------------------------------------------------------------------
// Styled components — follow the Emotion token pattern from FunnelModal
// ---------------------------------------------------------------------------

const PageWrapper = styled.div<{ $fullWidth?: boolean }>`
  min-height: 100vh;
  display: flex;
  align-items: ${({ $fullWidth }) => ($fullWidth ? 'stretch' : 'center')};
  justify-content: center;
  background: ${({ theme }) => theme.colors.background.primary};
  padding: ${({ $fullWidth }) => ($fullWidth ? '0' : '2rem 1rem')};
`;

const FullWidthContent = styled.article`
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Card = styled.article`
  width: 100%;
  max-width: 520px;
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.radius.regular};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  animation: landingFadeIn 0.25s ease;

  @keyframes landingFadeIn {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 300px;
  object-fit: cover;
  display: block;
`;

const ContentArea = styled.div`
  padding: 28px 28px 32px;
`;

const Headline = styled.h1`
  font-size: 26px;
  font-weight: 800;
  margin: 0 0 10px;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.25;
`;

const Description = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 20px;
  line-height: 1.6;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 24px;
`;

const CurrentPrice = styled.span`
  font-size: 32px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const OriginalPrice = styled.span`
  font-size: 20px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: line-through;
`;

const SaveBadge = styled.span`
  background: #dcfce7;
  color: #15803d;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
`;

const OfferSelector = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const OfferSelectorButton = styled.button<{ $selected: boolean }>`
  border: 1px solid ${({ $selected }) => ($selected ? '#111827' : '#d1d5db')};
  background: ${({ $selected }) => ($selected ? '#111827' : '#ffffff')};
  color: ${({ $selected }) => ($selected ? '#ffffff' : '#111827')};
  border-radius: 999px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`;

const AcceptButton = styled.button`
  width: 100%;
  padding: 15px;
  font-size: 17px;
  font-weight: 700;
  color: white;
  background: ${({ theme }) => theme.colors.text.primary};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.88;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DeclineLink = styled.button`
  display: block;
  margin: 14px auto 0;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const LoadingWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 15px;
`;

const FallbackWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 2rem;
  text-align: center;
`;

const FallbackHeading = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const FallbackText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const ErrorText = styled.p`
  margin: 14px 0 0;
  font-size: 14px;
  color: #b91c1c;
`;

const HomeLink = styled.a`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  text-decoration: underline;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LandingPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { localizedPath, countryConfig } = useIntl();
  const { acceptOffer, declineOffer, fetchTemplate } = useFunnelContext();
  const { cartToken, showCart } = useCartContext();
  const handleFunnelAddToCart = useFunnelAddToCart();
  const handleCampaignFreeGiftAccept = useCallback(async (campaign: RuntimeCampaign) => {
    const giftItem = extractFreeGiftItem(campaign);
    if (!giftItem || !giftItem.variantId) {
      throw new Error('Free gift campaign is missing product metadata');
    }
    await handleFunnelAddToCart(giftItem.productId, giftItem.variantId, giftItem.quantity);
  }, [handleFunnelAddToCart]);
  const [accepting, setAccepting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const pageUrl = `/lp/${slug ?? ''}`;
  const urlParameters = Object.fromEntries(searchParams.entries());

  const { loading, experience, offer, decline } = useFunnelExperience('landing_page', {
    url: pageUrl,
    country: countryConfig.countryCode,
    urlParameters: Object.keys(urlParameters).length > 0 ? urlParameters : undefined,
  });

  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);

  useEffect(() => {
    setSelectedOfferId(experience?.offers?.[0]?.id ?? experience?.offer?.id ?? null);
  }, [experience?.id, experience?.offers, experience?.offer?.id]);

  // Fetch template content when templateId is set and no inline pageContent exists.
  // Must be declared before any early returns to satisfy React hook rules.
  const [templateContent, setTemplateContent] = useState<StepPageContent | StepPageBlock[] | null>(null);
  const [templateLoadState, setTemplateLoadState] = useState<TemplateLoadState>('idle');

  useEffect(() => {
    if (!experience?.templateId) {
      setTemplateContent(null);
      setTemplateLoadState('idle');
      return;
    }

    // Clear stale template content immediately when templateId changes so
    // we can show an explicit loading state instead of the hardcoded card fallback.
    setTemplateContent(null);
    setTemplateLoadState('loading');
    let cancelled = false;

    const templateId = experience.templateId;
    fetchTemplate(templateId)
      .then((data) => {
        if (cancelled) return;
        // The API returns { pageContent: [...] } directly.
        // Also handle legacy shape { template: { componentTree: [...] } }.
        const content =
          data.pageContent ??
          (data as unknown as Record<string, Record<string, unknown>>).template?.componentTree ??
          (data as unknown as Record<string, Record<string, unknown>>).template?.pageContent;
        if (content) {
          const blocks = Array.isArray(content)
            ? content
            : (content as StepPageContent).blocks;
          setTemplateContent(blocks as StepPageBlock[]);
          setTemplateLoadState('ready');
          storefrontLayoutLogger.info('LandingPage loaded template content', {
            templateId,
            blockCount: Array.isArray(blocks) ? blocks.length : 0,
          });
        } else {
          setTemplateLoadState('failed');
          storefrontLayoutLogger.warn('LandingPage template has no content', { templateId });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setTemplateLoadState('failed');
        storefrontLayoutLogger.warn('LandingPage template fetch failed', {
          templateId,
          error: err instanceof Error ? err.message : String(err),
        });
      });

    return () => { cancelled = true; };
  }, [experience?.templateId, fetchTemplate]);

  const activeOffer: FunnelOffer | null =
    (selectedOfferId ? experience?.offers?.find((item) => item.id === selectedOfferId) ?? null : null) ??
    experience?.offers?.[0] ??
    offer ??
    null;
  const offerImage = useOfferImage(activeOffer?.imageUrl, activeOffer?.productId);

  const handleAccept = useCallback(async () => {
    if (!experience || !activeOffer) return;
    setAccepting(true);
    setActionError(null);
    try {
      if (!activeOffer.productId) {
        setActionError('This promotion is missing product information. Please contact support.');
        return;
      }
      void acceptOffer({
        stepId: experience.id,
        productId: activeOffer.productId,
        variantId: activeOffer.variantId ?? undefined,
        quantity: 1,
        action: 'add_to_cart',
      }).catch((err) => {
        storefrontLayoutLogger.warn('LandingPage accept API failed; continuing cart flow', {
          stepId: experience.id,
          productId: activeOffer.productId,
          error: err instanceof Error ? err.message : String(err),
        });
      });
      await handleFunnelAddToCart(activeOffer.productId, activeOffer.variantId ?? '', 1);
      showCart();
    } catch (err) {
      storefrontLayoutLogger.error('[LandingPage] accept failed', err instanceof Error ? err : undefined, {
        stepId: experience.id,
        productId: activeOffer.productId,
      });
      setActionError('We could not add this offer right now. Please try again.');
    } finally {
      setAccepting(false);
    }
  }, [experience, activeOffer, acceptOffer, handleFunnelAddToCart, showCart]);

  const handleDecline = useCallback(async () => {
    setActionError(null);
    if (experience) {
      try {
        await declineOffer(experience.id);
      } catch (err) {
        storefrontLayoutLogger.error('[LandingPage] decline failed', err instanceof Error ? err : undefined, {
          stepId: experience.id,
        });
        setActionError('We could not process this action right now. Please try again.');
        return;
      }
    }
    // Suppress this step for the rest of the session
    decline();
    navigate(localizedPath('/'));
  }, [experience, declineOffer, decline, navigate, localizedPath]);

  const campaignRuntime = (
    <CampaignErrorBoundary resetKey={pageUrl}>
      <CampaignExperience
        pageType="landing_page"
        context={{
          currentUrl: pageUrl,
          country: countryConfig.countryCode,
          urlParameters: Object.keys(urlParameters).length > 0 ? urlParameters : undefined,
        }}
        onFreeGiftAccept={handleCampaignFreeGiftAccept}
      />
    </CampaignErrorBoundary>
  );

  // --- Loading state ---
  if (loading) {
    return (
      <>
        {campaignRuntime}
        <LoadingWrapper>Loading offer...</LoadingWrapper>
      </>
    );
  }

  // --- No experience matched (funnel not assigned or step not found) ---
  if (!experience) {
    return (
      <>
        {campaignRuntime}
        <FallbackWrapper>
          <FallbackHeading>No offers available</FallbackHeading>
          <FallbackText>There are no active promotions for this page right now.</FallbackText>
          <HomeLink onClick={() => navigate(localizedPath('/'))}>Back to home</HomeLink>
        </FallbackWrapper>
      </>
    );
  }

  // --- Step matched but no offer configured ---
  if (!activeOffer) {
    return (
      <>
        {campaignRuntime}
        <FallbackWrapper>
          <FallbackHeading>Offer not configured</FallbackHeading>
          <FallbackText>This promotion is missing product information. Please contact support.</FallbackText>
          <HomeLink onClick={() => navigate(localizedPath('/'))}>Back to home</HomeLink>
        </FallbackWrapper>
      </>
    );
  }

  if (experience.templateId && templateLoadState === 'loading') {
    return (
      <>
        {campaignRuntime}
        <LoadingWrapper>Loading page...</LoadingWrapper>
      </>
    );
  }

  // Template priority: when templateId is set, template content WINS (same as FunnelExperience/PostPurchasePage)
  const pageContent = experience.templateId
    ? (templateContent ?? experience.pageContent ?? null)
    : (experience.pageContent ?? templateContent);
  const isFullWidth = experience.experienceType === 'page' || experience.layoutMode === 'full_width';
  const hasBlocks = Array.isArray(pageContent)
    ? pageContent.length > 0
    : Array.isArray(pageContent?.blocks) && (pageContent?.blocks?.length ?? 0) > 0;

  // Extract step-level actions and listeners (TF-209/TF-210)
  const stepActions = experience?.actions;
  const stepListeners = experience?.listeners;
  const triggerLifecycle = experience?.triggerLifecycle ?? 'session';

  const sideEffects = (
    <>
      {stepActions && stepActions.length > 0 && (
        <ActionExecutor
          actions={stepActions}
          stepId={experience.id}
          triggerLifecycle={triggerLifecycle}
          onAddToCart={(productId, variantId, quantity) =>
            handleFunnelAddToCart(productId, variantId, quantity)
          }
          onOfferAccept={async (offerId) => {
            setActionError(null);
            try {
              await acceptOffer({
                stepId: experience.id,
                productId: activeOffer?.productId ?? offerId,
                variantId: activeOffer?.variantId,
                quantity: 1,
                action: 'add_to_cart',
              });
            } catch (err) {
              storefrontLayoutLogger.error('[LandingPage] display_offer accept failed', err instanceof Error ? err : undefined, {
                stepId: experience.id,
                productId: activeOffer?.productId ?? offerId,
              });
              const message = 'We could not add this offer right now. Please try again.';
              setActionError(message);
              throw err instanceof Error ? err : new Error(message);
            }
          }}
          onOfferDecline={async () => {
            setActionError(null);
            try {
              await declineOffer(experience.id);
            } catch (err) {
              storefrontLayoutLogger.error('[LandingPage] display_offer decline failed', err instanceof Error ? err : undefined, {
                stepId: experience.id,
              });
              const message = 'We could not process this action right now. Please try again.';
              setActionError(message);
              throw err instanceof Error ? err : new Error(message);
            }
          }}
        />
      )}
      {stepListeners && stepListeners.length > 0 && (
        <ListenerBinder
          listeners={stepListeners}
          stepId={experience.id}
          triggerLifecycle={triggerLifecycle}
        />
      )}
    </>
  );

  // When page_content blocks are configured, render the block-based layout
  if (hasBlocks) {
    return (
      <>
        {campaignRuntime}
        <PageWrapper $fullWidth={isFullWidth}>
          <FunnelPageContent
            offer={activeOffer}
            offers={experience.offers}
            selectedOfferId={selectedOfferId ?? undefined}
            onSelectOffer={setSelectedOfferId}
            pageContent={pageContent}
            fullPage={isFullWidth}
            accepting={accepting}
            onAccept={handleAccept}
            onDecline={handleDecline}
            onDismiss={handleDecline}
          />
        </PageWrapper>
        {sideEffects}
      </>
    );
  }

  // Fallback: hardcoded layout when no page_content blocks are configured
  const savings =
    activeOffer.originalPrice != null && activeOffer.pricingValue != null
      ? activeOffer.originalPrice - activeOffer.pricingValue
      : 0;
  const safePx = (v: number | null | undefined) => Number.isFinite(v) ? `$${(v as number).toFixed(2)}` : '$0.00';

  const content = (
    <>
      {offerImage && (
        <HeroImage src={offerImage} alt={activeOffer.headline} style={isFullWidth ? { height: '50vh', maxHeight: 600 } : undefined} />
      )}

      <ContentArea style={isFullWidth ? { maxWidth: 720, margin: '0 auto' } : undefined}>
        {Array.isArray(experience.offers) && experience.offers.length > 1 && (
          <OfferSelector aria-label="Offer selector">
            {experience.offers.map((item, index) => {
              const isSelected = (selectedOfferId ?? activeOffer.id) === item.id || (!selectedOfferId && index === 0);
              return (
                <OfferSelectorButton
                  key={item.id}
                  type="button"
                  $selected={isSelected}
                  aria-pressed={isSelected}
                  onClick={() => setSelectedOfferId(item.id)}
                >
                  {item.name || item.headline || item.id}
                </OfferSelectorButton>
              );
            })}
          </OfferSelector>
        )}
        <Headline>{activeOffer.headline}</Headline>

        {activeOffer.description && (
          <Description>{activeOffer.description}</Description>
        )}

        <PriceRow>
          <CurrentPrice aria-label={`Discounted price: ${safePx(activeOffer.pricingValue)}`}>
            {safePx(activeOffer.pricingValue)}
          </CurrentPrice>
          <OriginalPrice aria-label={`Original price: ${safePx(activeOffer.originalPrice)}`}>
            {safePx(activeOffer.originalPrice)}
          </OriginalPrice>
          {savings > 0 && (
            <SaveBadge>Save {safePx(savings)}</SaveBadge>
          )}
        </PriceRow>

        <AcceptButton onClick={handleAccept} disabled={accepting} aria-busy={accepting}>
          {accepting ? 'Adding to cart...' : activeOffer.ctaText}
        </AcceptButton>
        {actionError && <ErrorText role="alert">{actionError}</ErrorText>}

        <DeclineLink onClick={handleDecline}>
          {activeOffer.declineText}
        </DeclineLink>
      </ContentArea>
    </>
  );

  const Wrapper = isFullWidth ? FullWidthContent : Card;

  return (
    <>
      {campaignRuntime}
      <PageWrapper $fullWidth={isFullWidth}>
        <Wrapper>{content}</Wrapper>
      </PageWrapper>
      {sideEffects}
    </>
  );
}
